import React, { useState } from 'react';
import type { MutableRefObject } from 'react';
import { useSelector } from 'react-redux';

import FormulaDropdown from './FormulaDropdown';
import TableFormulaSheet from './TableFormulaSheet';
import TableDatePickerSheet from './TableDatePickerSheet';
import DatePickerContainer from './cellTypes/DatePickerContainer';
import useTableDatePicker from './cellTypes/useTableDatePicker';
import { selectIsMobileAppMode } from '../../../platform/platformSelector';
import SimpleDateTimePicker from '../../../components/datetime/SimpleDateTimePicker';

import { MNHotInstance } from '../bridge/tableHotInstanceTypes';
import type { FormulaPickerRef } from './useFormulaPickerController';

type TablePopupsProps = {
    cellEditingSessionId?: number;
    gridSize: number;
    isActiveTable: boolean;
    isCurrentlyEditingTableCell: boolean;
    formulaPickerRef: MutableRefObject<FormulaPickerRef | null>;
    locale: string;
    hotTableInstanceRef: MutableRefObject<MNHotInstance | null>;
};

const TablePopups: React.FC<TablePopupsProps> = ({
    cellEditingSessionId,
    gridSize,
    isActiveTable,
    isCurrentlyEditingTableCell,
    formulaPickerRef,
    locale,
    hotTableInstanceRef,
}) => {
    const isMobileAppMode = useSelector(selectIsMobileAppMode);

    const addFormulaToCell = (formula: string, replacePrevCharacters: number) => {
        if (!formula) return;

        const editor = hotTableInstanceRef.current?.getMNEditor();
        if (!editor) return;

        editor.insertPlainTextAtSelection(`${formula}(`, {
            replacePrevCharacters,
        });
    };

    const refocusCellEditor = () => {
        hotTableInstanceRef.current?.getMNEditor()?.focus();
    };

    const [showFormulaPicker, setShowFormulaPicker] = useState(false);
    const { dateTimePickerProps, showDatePicker } = useTableDatePicker({
        isCurrentlyEditingTableCell,
        locale,
        showFormulaPicker,
        hotTableInstanceRef,
    });

    if (!isMobileAppMode) {
        return (
            <>
                <FormulaDropdown
                    ref={formulaPickerRef}
                    addFormulaToCell={addFormulaToCell}
                    show={showFormulaPicker}
                    setShow={setShowFormulaPicker}
                />
                <DatePickerContainer gridSize={gridSize} show={showDatePicker}>
                    <SimpleDateTimePicker {...dateTimePickerProps} />
                </DatePickerContainer>
            </>
        );
    }

    if (!isActiveTable) return null;

    return (
        <>
            <TableFormulaSheet
                addFormulaToCell={addFormulaToCell}
                formulaPickerRef={formulaPickerRef}
                refocusCellEditor={refocusCellEditor}
                show={showFormulaPicker}
                setShow={setShowFormulaPicker}
            />
            <TableDatePickerSheet
                showSessionId={showDatePicker ? cellEditingSessionId : undefined}
                onFinishEditing={() => hotTableInstanceRef.current?.getActiveEditor()?.finishEditing()}
            >
                <SimpleDateTimePicker {...dateTimePickerProps} />
            </TableDatePickerSheet>
        </>
    );
};

export default TablePopups;
